import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Popover, Slider, TextField } from "@mui/material"

import BasicButton from "@/components/button/BasicButton"
import Icon from "@/components/icon/Icon"
import { colors } from "@/components/colors"

import "./columnFilterPopover.scss"

type ColumnFilterPopoverProps = {
  open: boolean
  anchorPosition?: { top: number; left: number }
  onClose: () => void
  columnName: string
  unit?: string | null
  minValue: number
  maxValue: number
  currentRange: [number, number]
  onApply: (range: [number, number]) => void
}

export default function ColumnFilterPopover({
  open,
  anchorPosition,
  onClose,
  columnName,
  unit,
  minValue,
  maxValue,
  currentRange,
  onApply,
}: ColumnFilterPopoverProps) {
  const { t } = useTranslation()
  const [tempRange, setTempRange] = useState<[number, number]>(currentRange)
  const [minInput, setMinInput] = useState<string>(currentRange[0].toString())
  const [maxInput, setMaxInput] = useState<string>(currentRange[1].toString())

  useEffect(() => {
    if (open) {
      setTempRange(currentRange)
      setMinInput(currentRange[0].toString())
      setMaxInput(currentRange[1].toString())
    }
  }, [open, currentRange])

  const handleSliderChange = (_: Event, newValue: number | number[]) => {
    const range = newValue as [number, number]
    setTempRange(range)
    setMinInput(range[0].toString())
    setMaxInput(range[1].toString())
  }

  const handleMinInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setMinInput(value)

    const numValue = parseFloat(value)
    if (!isNaN(numValue) && numValue >= minValue && numValue <= tempRange[1]) {
      setTempRange([numValue, tempRange[1]])
    }
  }

  const handleMaxInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setMaxInput(value)

    const numValue = parseFloat(value)
    if (!isNaN(numValue) && numValue <= maxValue && numValue >= tempRange[0]) {
      setTempRange([tempRange[0], numValue])
    }
  }

  const handleMinInputBlur = () => {
    const numValue = parseFloat(minInput)
    if (isNaN(numValue) || numValue < minValue) {
      setMinInput(minValue.toString())
      setTempRange([minValue, tempRange[1]])
    } else if (numValue > tempRange[1]) {
      setMinInput(tempRange[1].toString())
      setTempRange([tempRange[1], tempRange[1]])
    }
  }

  const handleMaxInputBlur = () => {
    const numValue = parseFloat(maxInput)
    if (isNaN(numValue) || numValue > maxValue) {
      setMaxInput(maxValue.toString())
      setTempRange([tempRange[0], maxValue])
    } else if (numValue < tempRange[0]) {
      setMaxInput(tempRange[0].toString())
      setTempRange([tempRange[0], tempRange[0]])
    }
  }

  const handleReset = () => {
    setTempRange([minValue, maxValue])
    setMinInput(minValue.toString())
    setMaxInput(maxValue.toString())
  }

  const handleApply = () => {
    onApply(tempRange)
    onClose()
  }

  const displayUnit = unit ? `(${unit})` : ""
  const isChanged = tempRange[0] !== currentRange[0] || tempRange[1] !== currentRange[1]

  return (
    <Popover
      open={open}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      onClose={onClose}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      slotProps={{
        paper: {
          className: "ColumnFilterPopoverPaper",
        },
      }}>
      <div className="ColumnFilterPopoverCont">
        <p className="column-filter-title">
          {columnName} {displayUnit}
        </p>

        <div className="column-filter-inputs">
          <TextField
            className="column-filter-input"
            value={minInput}
            onChange={handleMinInputChange}
            onBlur={handleMinInputBlur}
            size="small"
            type="number"
            inputProps={{
              min: minValue,
              max: tempRange[1],
              step: "any",
            }}
          />
          <span className="column-filter-separator">~</span>
          <TextField
            className="column-filter-input"
            value={maxInput}
            onChange={handleMaxInputChange}
            onBlur={handleMaxInputBlur}
            size="small"
            type="number"
            inputProps={{
              min: tempRange[0],
              max: maxValue,
              step: "any",
            }}
          />
        </div>

        <div className="column-filter-slider">
          <Slider
            value={tempRange}
            onChange={handleSliderChange}
            min={minValue}
            max={maxValue}
            step={(maxValue - minValue) / 100 || 0.01}
            valueLabelDisplay="off"
            marks={[{ value: minValue }, { value: maxValue }]}
            sx={{
              color: colors.platinum[900],
              height: "8px",
              "& .MuiSlider-thumb": {
                width: "16px",
                height: "16px",
                backgroundColor: colors.neutral.white,
                border: `2px solid ${colors.platinum["900"]}`,
              },
              "& .MuiSlider-track": {
                height: "8px",
                border: "none",
              },
              "& .MuiSlider-rail": {
                height: "8px",
                backgroundColor: colors.neutral.grey[300],
                opacity: 1,
              },
              "& .MuiSlider-mark": {
                width: "1px",
                height: "4px",
                backgroundColor: colors.neutral.grey[400],
                marginTop: "8px",
              },
            }}
          />
          <div className="column-filter-slider-labels">
            <span>{minValue}</span>
            <span>{maxValue.toLocaleString()}</span>
          </div>
        </div>

        <div className="column-filter-buttons">
          <BasicButton type="default-outlined" size="sm" onClick={handleReset}>
            <Icon type="refresh" size={16} color={colors.neutral.grey[700]} />
            {t("button.reset")}
          </BasicButton>
          <BasicButton
            type="primary-contained"
            size="sm"
            onClick={handleApply}
            disabled={!isChanged}>
            {t("button.apply")}
          </BasicButton>
        </div>
      </div>
    </Popover>
  )
}
